/**
 * Audio player module
 * Handles audio playback, seeking, and keyboard shortcuts
 */

import { showNotification } from '../components/notifications.js';
import { formatTime } from '../core/utils.js';

// Re-export formatTime for backward compatibility
export { formatTime };

/**
 * Seek to specific time in audio player
 */
export function seekToTime(seconds) {
    const audioElement = document.getElementById('episode-audio');
    if (audioElement && seconds !== null && seconds !== undefined) {
        audioElement.currentTime = seconds;
        // Start playing if paused
        if (audioElement.paused) {
            audioElement.play();
        }
        // Visual feedback - briefly highlight the audio player
        const audioContainer = audioElement.parentElement;
        if (audioContainer) {
            audioContainer.style.transition = 'background-color 0.3s';
            audioContainer.style.backgroundColor = 'rgba(52, 152, 219, 0.2)';
            setTimeout(() => {
                audioContainer.style.backgroundColor = '';
            }, 500);
        }
    }
}

/**
 * Toggle between speaker diarized and raw text format
 */
export function toggleTranscriptionFormat() {
    const diarized = document.getElementById('transcription-diarized');
    const raw = document.getElementById('transcription-raw');
    const btn = document.getElementById('format-toggle-btn');

    if (diarized && raw) {
        if (diarized.style.display === 'none') {
            diarized.style.display = 'block';
            raw.style.display = 'none';
            if (btn) btn.innerHTML = '📄 Show Raw Text';
        } else {
            diarized.style.display = 'none';
            raw.style.display = 'block';
            if (btn) btn.innerHTML = '🎤 Show Speaker View';
        }
    }
}

/**
 * Copy transcription to clipboard
 */
export async function copyTranscription() {
    let textToCopy = '';

    // Check which format is visible
    const diarized = document.getElementById('transcription-diarized');
    const raw = document.getElementById('transcription-raw');

    if (raw && raw.style.display !== 'none') {
        textToCopy = raw.textContent;
    } else if (diarized && window.currentEpisodeData && window.currentEpisodeData.transcription_data) {
        // Format speaker diarized text for copying
        const segments = window.currentEpisodeData.transcription_data.segments_with_speakers;
        let currentSpeaker = null;
        let formattedText = [];

        segments.forEach(seg => {
            if (seg.speaker !== currentSpeaker) {
                formattedText.push(`\n${seg.speaker}:\n`);
                currentSpeaker = seg.speaker;
            }
            formattedText.push(seg.text);
        });

        textToCopy = formattedText.join(' ');
    } else if (window.currentEpisodeData) {
        textToCopy = window.currentEpisodeData.transcription || '';
    }

    try {
        await navigator.clipboard.writeText(textToCopy);
        showNotification('Transcription copied to clipboard!', 'success');
    } catch (err) {
        console.error('Failed to copy:', err);
        showNotification('Failed to copy to clipboard', 'error');
    }
}

/**
 * Setup keyboard shortcuts for audio player in modal
 */
export function setupAudioKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
        const modal = document.getElementById('episode-modal');
        const audioElement = document.getElementById('episode-audio');

        // Only handle shortcuts when modal is active
        if (!modal || !modal.classList.contains('active')) return;

        // Don't handle shortcuts when typing in input fields
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        switch(e.key) {
            case 'Escape':
                if (window.closeModal) {
                    window.closeModal();
                }
                break;

            case ' ':  // Spacebar
                if (audioElement) {
                    e.preventDefault(); // Prevent page scroll
                    if (audioElement.paused) {
                        audioElement.play();
                    } else {
                        audioElement.pause();
                    }
                }
                break;

            case 'ArrowLeft':
                if (audioElement) {
                    e.preventDefault();
                    audioElement.currentTime = Math.max(0, audioElement.currentTime - 10);
                }
                break;

            case 'ArrowRight':
                if (audioElement) {
                    e.preventDefault();
                    audioElement.currentTime = Math.min(audioElement.duration, audioElement.currentTime + 10);
                }
                break;
        }
    });
}

/**
 * Setup audio time update listener for highlighting current segment
 * @param {function} onTimeUpdate - Callback with current time
 */
export function setupAudioTimeTracking(onTimeUpdate) {
    const audioElement = document.getElementById('episode-audio');
    if (!audioElement) return;

    let lastHighlightedSegment = null;

    audioElement.addEventListener('timeupdate', () => {
        const currentTime = audioElement.currentTime;

        // Find the segment that matches current time
        const segments = document.querySelectorAll('[data-segment-start]');
        let currentSegment = null;

        segments.forEach(seg => {
            const start = parseFloat(seg.dataset.segmentStart);
            const end = parseFloat(seg.dataset.segmentEnd);
            if (currentTime >= start && currentTime < end) {
                currentSegment = seg;
            }
        });

        // Update highlighting
        if (currentSegment !== lastHighlightedSegment) {
            // Remove highlight from previous
            if (lastHighlightedSegment) {
                lastHighlightedSegment.style.backgroundColor = '';
                lastHighlightedSegment.style.transition = '';
            }

            // Add highlight to current
            if (currentSegment) {
                currentSegment.style.transition = 'background-color 0.3s';
                currentSegment.style.backgroundColor = 'rgba(52, 152, 219, 0.1)';

                // Scroll into view if needed
                currentSegment.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }

            lastHighlightedSegment = currentSegment;
        }

        if (onTimeUpdate) {
            onTimeUpdate(currentTime);
        }
    });
}

/**
 * Initialize audio player functionality
 */
export function initAudioPlayer() {
    setupAudioKeyboardShortcuts();
}
